import { useRef, useState, useCallback } from 'react';
import type { BudgetData, ImportResult } from '../../types';
import {
  checkImportConflicts,
  decryptAndCheckImport,
  importData,
  type ImportMode,
  type CheckImportResult,
} from '../../services/exportService';
import type { EncryptedPayload } from '../../services/encryptionService';

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

type DialogState =
  | { step: 'idle' }
  | { step: 'loading' }
  | { step: 'encrypted'; encryptedPayload: EncryptedPayload; file: File }
  | {
      step: 'conflict';
      result: ImportResult;
      file: File;
      decryptedData?: BudgetData;
    }
  | { step: 'error'; message: string };

export function ImportDialog({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: ImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<DialogState>({ step: 'idle' });
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const resetAndClose = useCallback(() => {
    setState({ step: 'idle' });
    setPassword('');
    setPasswordError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  }, [onClose]);

  const processCheckResult = useCallback(
    async (
      result: CheckImportResult,
      file: File,
      decryptedData?: BudgetData
    ) => {
      if (result.status === 'error') {
        setState({
          step: 'error',
          message: result.error ?? 'Error desconocido',
        });
        return;
      }

      if (result.status === 'encrypted') {
        setState({
          step: 'encrypted',
          encryptedPayload: result.encryptedPayload,
          file,
        });
        return;
      }

      if (result.status === 'conflict') {
        const resultWithData = result as ImportResult & {
          decryptedData?: BudgetData;
        };
        setState({
          step: 'conflict',
          result,
          file,
          decryptedData: decryptedData || resultWithData.decryptedData,
        });
        return;
      }

      const resultWithData = result as ImportResult & {
        decryptedData?: BudgetData;
      };
      const importResult = await importData(
        file,
        'replace',
        decryptedData || resultWithData.decryptedData
      );
      if (importResult.status === 'success' && importResult.imported) {
        const { transactions, categories, budgets } = importResult.imported;
        onSuccess(
          `Importadas ${transactions} transacciones, ${categories} categorías, ${budgets} presupuestos`
        );
        resetAndClose();
      } else {
        onError(importResult.error ?? 'Error al importar');
        resetAndClose();
      }
    },
    [onSuccess, onError, resetAndClose]
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setState({ step: 'loading' });
      setPassword('');
      setPasswordError(null);

      const result = await checkImportConflicts(file);
      await processCheckResult(result, file);
    },
    [processCheckResult]
  );

  const handleDecrypt = useCallback(async () => {
    if (state.step !== 'encrypted') return;

    setState({ step: 'loading' });
    setPasswordError(null);

    const result = await decryptAndCheckImport(
      state.encryptedPayload,
      password,
      state.file
    );

    if (result.status === 'error') {
      setPasswordError(result.error ?? 'Contraseña incorrecta');
      setState({
        step: 'encrypted',
        encryptedPayload: state.encryptedPayload,
        file: state.file,
      });
      return;
    }

    const resultWithData = result as ImportResult & {
      decryptedData?: BudgetData;
    };
    await processCheckResult(result, state.file, resultWithData.decryptedData);
  }, [state, password, processCheckResult]);

  const handleImport = useCallback(
    async (mode: ImportMode) => {
      if (state.step !== 'conflict') return;

      setState({ step: 'loading' });
      const result = await importData(state.file, mode, state.decryptedData);

      if (result.status === 'success' && result.imported) {
        const { transactions, categories, budgets } = result.imported;
        onSuccess(
          `Importadas ${transactions} transacciones, ${categories} categorías, ${budgets} presupuestos`
        );
        resetAndClose();
      } else {
        onError(result.error ?? 'Error al importar');
        resetAndClose();
      }
    },
    [state, onSuccess, onError, resetAndClose]
  );

  const handleOpenFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={resetAndClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-dialog-title"
      >
        <h2
          id="import-dialog-title"
          className="text-xl font-bold text-near-black mb-4"
        >
          Importar datos
        </h2>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          className="hidden"
          aria-label="Seleccionar archivo JSON"
        />

        {state.step === 'idle' && (
          <div className="space-y-4">
            <p className="text-mid-gray">
              Selecciona un archivo JSON exportado previamente desde Budgeter.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={resetAndClose}
                className="px-4 py-2 text-mid-gray hover:text-near-black transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleOpenFilePicker}
                className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition"
              >
                Seleccionar archivo
              </button>
            </div>
          </div>
        )}

        {state.step === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            <span className="ml-3 text-mid-gray">Procesando...</span>
          </div>
        )}

        {state.step === 'encrypted' && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
              <span className="text-blue-600 text-lg">🔒</span>
              <p className="text-sm text-blue-800">
                Este archivo está protegido con contraseña
              </p>
            </div>

            <div>
              <label
                htmlFor="import-password"
                className="block text-sm text-mid-gray mb-1"
              >
                Contraseña:
              </label>
              <input
                id="import-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && password) {
                    handleDecrypt();
                  }
                }}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-brand-primary ${
                  passwordError ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Introduce la contraseña"
                autoFocus
              />
              {passwordError && (
                <p className="text-sm text-red-500 mt-1">{passwordError}</p>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={resetAndClose}
                className="px-4 py-2 text-mid-gray hover:text-near-black transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleDecrypt}
                disabled={!password}
                className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Descifrar
              </button>
            </div>
          </div>
        )}

        {state.step === 'error' && (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <p className="text-red-700">{state.message}</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={resetAndClose}
                className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        {state.step === 'conflict' && state.result.imported && (
          <div className="space-y-4">
            <p className="text-mid-gray">
              Ya tienes datos existentes. ¿Qué deseas hacer?
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-gray-50 rounded">
                <p className="font-medium text-near-black mb-2">
                  Datos actuales
                </p>
                <ul className="text-mid-gray space-y-1">
                  <li>
                    {state.result.existingCounts?.transactions ?? 0}{' '}
                    transacciones
                  </li>
                  <li>
                    {state.result.existingCounts?.categories ?? 0} categorías
                  </li>
                  <li>
                    {state.result.existingCounts?.budgets ?? 0} presupuestos
                  </li>
                </ul>
              </div>
              <div className="p-3 bg-blue-50 rounded">
                <p className="font-medium text-near-black mb-2">
                  Datos a importar
                </p>
                <ul className="text-mid-gray space-y-1">
                  <li>{state.result.imported.transactions} transacciones</li>
                  <li>{state.result.imported.categories} categorías</li>
                  <li>{state.result.imported.budgets} presupuestos</li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleImport('replace')}
                className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Reemplazar todo
              </button>
              <button
                onClick={() => handleImport('merge')}
                className="w-full px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition"
              >
                Combinar datos
              </button>
              <button
                onClick={resetAndClose}
                className="w-full px-4 py-2 text-mid-gray hover:text-near-black transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
